import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Snackbar, Grid, FormControlLabel, Checkbox } from "@material-ui/core";
import { Dialog, Item, Switch } from "@zebra-fed/zds-react-desktop";
import { SnackbarContent } from "../../styled-components/shared";
import styled from "styled-components";
import { AuthContext } from "../../authentication/Auth";
import { DataContext } from "../../data/DataContext";
import { usePermissions } from "../../authentication/rbac";
import { InputField } from "../common/InputField";
import { DropdownField } from "../common/DropdownField";
import { getEodCategories, EOD_CATEGORIES } from "../../data/constants";
import { TimeZoneData } from "../../data/tzTimeZoneList";
import { SiteService } from "../../Services/SiteService";
import { LabelText, Formcontainer, StyledTextField } from "./Styles";
import { SiteReportedTime } from "./EditSiteModal";
import { FieldWrapper } from "../common/FieldWrapper";
import { isIPAddress } from "ip-address-validator";
import app from "../../authentication/firebaseStart";

const firestore = app.firestore();

const ModalHeader = styled.div`
  display: flex;
  height: 60px;
  font-size: 18px;
  font-weight: 500;
  align-items: center;
  padding: 0 24px;
  justify-content: left;
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 1;
`;

const ScrollableContent = styled.div`
  max-height: calc(100vh - 200px);
  overflow-y: auto;
  padding: 24px;
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionHeader = styled.h2`
  font-size: 14px;
  font-weight: 400;
  margin: 0;
  color: #636e72;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
`;

export default function AddNewSiteModal({
  modalRef,
  onClose,
  appSetting,
  data = [],
  initialData = null,
}) {
  const { t } = useTranslation();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const context = useContext(AuthContext);
  const companyID = context.currentUser.claims.companyID;
  const {
    canCreateSite,
    canUpdateSite,
    canCreateEmailConfiguration,
    canUpdateEmailConfiguration,
    canDeleteEmailConfiguration,
  } = usePermissions();
  const service = new SiteService();
  const { deviceRegistrationType } = appSetting;
  const { dlrFeatureFlag, mlPredictionStatus, globalAppSettings } =
    useContext(DataContext);
  const timeZoneOptions = TimeZoneData.map((tz) => ({
    value: tz.tzDatabaseName,
    label: tz.tzDatabaseName,
  }));
  const categoriesData = getEodCategories(
    globalAppSettings.enableBTScannerTracking
  );
  const [showDLR, setShowDLR] = useState(false);

   useEffect(() => {
          console.log("categoriesData ", categoriesData, dlrFeatureFlag)
          if(!dlrFeatureFlag){
            // removing At Risk if dlr feature is disabled
            setShowDLR(false);
          }
          else{
            if(mlPredictionStatus)  setShowDLR(true);    // adding At Risk if ml prediction enabled
            else  setShowDLR(false);
          }
      }, [mlPredictionStatus, dlrFeatureFlag]);

  const validationSchema = Yup.object().shape({
    siteName: Yup.string()
      .required(t("siteNameRequired"))
      .min(1, t("siteNameMinLength"))
      .max(50, t("siteNameMaxLength"))
      .matches(
        /^[a-zA-Z0-9'_ -]+(\s[a-zA-Z0-9'_ -]+)*$/,
        t("enterValidSiteName")
      )
      .test("trimmed-validation", t("enterValidSiteName"), function (value) {
        return (value?.trim().length ?? 0) > 0;
      })
      .test(
        "is-duplicate-site-name",
        t("validation.rules.sso.rule49"),
        async (value) => {
          if (!value || initialData) return true;
          const site = await firestore
            .collection("tenants")
            .doc(companyID)
            .collection("SiteLocations")
            .where("siteName", "==", value)
            .get();
          return site.docs.length === 0;
        }
      ),
    displaySiteName: Yup.string()
      .max(50, t("siteLabelMaxLength"))
      .matches(
        /^[a-zA-Z0-9'_ -]+(\s[a-zA-Z0-9'_ -]+)*$/,
        t("enterValidSiteLabel")
      ),
    address_line: Yup.string()
      .required(t("address1Required"))
      .min(1, t("address1MinLength"))
      .max(50, t("address1MaxLength"))
      .matches(
        /^[a-zA-Z0-9'_ -]+(\s[a-zA-Z0-9'_ -]+)*$/,
        t("enterValidAddress1")
      )
      .test("trimmed-validation", t("enterValidAddress1"), function (value) {
        return (
          (value === null || value === undefined
            ? false
            : value.trim().length) > 0
        );
      }),
    address2: Yup.string()
      .max(50, t("address2MaxLength"))
      .matches(
        /^[a-zA-Z0-9' _-]+(\s[a-zA-Z0-9' _-]+)*$/,
        t("enterValidAddress2")
      ),
    postal_code: Yup.string()
      .max(10, t("postalCodeMaxLength"))
      .matches(/^[a-zA-Z0-9]+(\s[a-zA-Z0-9]+)*$/, t("enterValidPostalCode")),
    town: Yup.string()
      .max(50, t("townMaxLength"))
      .matches(/^[a-zA-Z0-9' _-]+(\s[a-zA-Z0-9' _-]+)*$/, t("enterValidTown")),
    city: Yup.string()
      .required(t("cityRequired"))
      .min(1, t("cityMinLength"))
      .max(50, t("cityMaxLength"))
      .matches(/^[a-zA-Z0-9' _-]+(\s[a-zA-Z0-9' _-]+)*$/, t("enterValidCity"))
      .test("trimmed-validation", t("enterValidCity"), function (value) {
        return (
          (value === null || value === undefined
            ? false
            : value.trim().length) > 0
        );
      }),
    country: Yup.string()
      .required(t("countryRequired"))
      .min(1, t("countryMinLength"))
      .max(50, t("countryMaxLength"))
      .matches(/^[a-zA-Z0-9' -]+(\s[a-zA-Z0-9' -]+)*$/, t("enterValidCountry"))
      .test(
        "trimmed-validation",
        t("enterValidCountry"),
        (value) => value?.trim().length > 0
      ),
    contact_first_name: Yup.string()
      .max(50, t("firstNameMaxLength"))
      .matches(
        /^[a-zA-Z0-9' -]+(\s[a-zA-Z0-9' -]+)*$/,
        t("enterValidFirstName")
      ),
    contact_last_name: Yup.string()
      .max(50, t("lastNameMaxLength"))
      .matches(
        /^[a-zA-Z0-9' -]+(\s[a-zA-Z0-9' -]+)*$/,
        t("enterValidLastName")
      ),
    contact_mobile_number: Yup.string()
      .max(10, t("phoneNumberMaxLength"))
      .matches(/^[0-9]+(\s[0-9]+)*$/, t("enterValidPhoneNumber")),
    contact_phone_number: Yup.string()
      .max(10, t("mobileNumberMaxLength"))
      .matches(/^[0-9]+(\s[0-9]+)*$/, t("enterValidMobileNumber")),
    contact_extension: Yup.string()
      .max(10, t("extensionMaxLength"))
      .matches(/^[0-9]+(\s[0-9]+)*$/, t("enterValidExtension")),
    contact_country: Yup.string()
      .max(50, t("contactCountryMaxLength"))
      .matches(
        /^[a-zA-Z0-9' -]+(\s[a-zA-Z0-9' -]+)*$/,
        t("enterValidContactCountry")
      ),
    // alarm_timeout: Yup.number(t("enterValidAlarmTimeout"))
    //   .positive(t("enterValidAlarmTimeout"))
    //   .required(t("alarmTimeoutRequired"))
    //   .min(1, t("alarmTimeoutMinLength")),
    startIPAddress: Yup.string()
      .when("$deviceRegistrationType", {
        is: 2,
        then: (schema) =>
          schema.required(t("validation.rules.Mo_Dash.RequiredTxtMessage")),
        otherwise: (schema) => schema.notRequired(),
      })
      .test(
        "is-valid-ip",
        t("validation.rules.Mo_Dash.InvalidIPAddress"),
        (value) => {
          if (deviceRegistrationType != 2) return true;
          return value && isIPAddress(value);
        }
      ),
    endIPAddress: Yup.string()
      .when("$deviceRegistrationType", {
        is: 2,
        then: (schema) =>
          schema.required(t("validation.rules.Mo_Dash.RequiredTxtMessage")),
        otherwise: (schema) => schema.notRequired(),
      })
      .test(
        "is-valid-ip",
        t("validation.rules.Mo_Dash.InvalidIPAddress"),
        (value) => {
          if (deviceRegistrationType != 2) return true;
          return value && isIPAddress(value);
        }
      )
      .test(
        "is-valid-range",
        t("validation.rules.Mo_Dash.InvalidIPAddressRange"),
        function (endIP, context) {
          if (deviceRegistrationType != 2) return true;
          const startIPAddress = context?.parent?.startIPAddress;
          return SiteService.isValidIPRange(startIPAddress, endIP);
        }
      )
      .test(
        "is-not-same",
        t("validation.rules.Mo_Dash.IpAddressOverlap"),
        async function (endIP, context) {
          if (deviceRegistrationType != 2) return true;
          const startIPAddress = context?.parent?.startIPAddress;
          const sites = initialData
            ? data.filter((element) => element.siteName != initialData.siteName)
            : data;
          const res = await service.ipRangeOverlapping(
            startIPAddress,
            endIP,
            sites
          );
          return !res;
        }
      ),
    timeZone: Yup.string().required(t("timeZoneRequired")),
    emailSubscribed: Yup.boolean(),
    snapshotReportCategories: Yup.array()
      .when("emailSubscribed", {
        is: true,
        then: (schema) => {
          schema.required(t("snapshotReportCategoriesRequired"));
        },
        otherwise: (schema) => schema.notRequired(),
      })
      .test(
        "is-valid-report-list",
        t("snapshotReportCategoriesRequired"),
        (value) => {
          if (!value || value.length == 0) return false;
          return true;
        }
      ),
    emailList: Yup.string()
      .when("emailSubscribed", {
        is: true,
        then: (schema) => schema.required(t("emailListRequired")),
        otherwise: (schema) => schema.notRequired(),
      })
      .test("is-valid-email-list", t("enterValidEmail"), (value) => {
        if (!value) return true;
        return SiteService.isValidEmailList(value);
      }),
  });

  const stringToReportArray = (values) => {
    if (!values) return [];
    const reportCategories = values?.split(",");
    if (reportCategories.includes("") || reportCategories.length === 0) return [];
    if (reportCategories.includes("All")) return [categoriesData[0]];
    return reportCategories.map((category) => ({
      label: category,
      value: category,
    }));
  };

  const setSnapshotReportCategories = (initialData) => {
    if (!initialData) return [categoriesData[0]];
    if (initialData.snapshotReportCategories === "Default" || initialData.snapshotReportCategories === "[object Object]")
      return [categoriesData[0]];
    if (initialData.snapshotReportCategories?.length > 0)
      return stringToReportArray(initialData?.snapshotReportCategories);
  };

  const getInitialValues = () => {
    return {
      siteName: initialData?.siteName || "",
      displaySiteName: initialData?.displaySiteName || "",
      address_line: initialData?.address_line || "",
      address2: initialData?.address2 || "",
      postal_code: initialData?.postal_code || "",
      town: initialData?.town || "",
      city: initialData?.city || "",
      country: initialData?.country || "",
      contact_first_name: initialData?.contact_first_name || "",
      contact_last_name: initialData?.contact_last_name || "",
      contact_mobile_number: initialData?.contact_mobile_number || "",
      contact_phone_number: initialData?.contact_phone_number || "",
      contact_extension: initialData?.contact_extension || "",
      contact_country: initialData?.contact_country || "",
      // alarm_timeout: initialData?.alarm_timeout || 0,
      // alarm_timeout_inherited: initialData?.alarm_timeout_inherited || false,
      startIPAddress: initialData?.startIPAddress || "",
      endIPAddress: initialData?.endIPAddress || "",
      timeZone: initialData?.timeZone || timeZoneOptions[0]?.value,
      emailSubscribed: initialData?.emailSubscribed || false,
      scheduledReportTime: {
        hrs: initialData?.reportHoursActual || 0,
        minutes: initialData?.reportMinutesActual || 0,
      },
      snapshotReportCategories: setSnapshotReportCategories(initialData),
      emailList: initialData?.emailList || "",
      attachCSV: initialData?.attachCSV || false,
    };
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema: validationSchema,
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validationContext: { deviceRegistrationType },
    onSubmit: handleSubmit,
  });

  const arrayToObject = (array) => {
    return array.reduce((obj, key) => {
      obj[key] = true;
      return obj;
    }, {});
  };

  async function handleSubmit() {
    setIsLoading(true);
    // const companyID = context.currentUser.claims.companyID;
    // formik.values.alarm_timeout =
    //   typeof formik.values.alarm_timeout == "string" &&
    //   formik.values.alarm_timeout?.trim().length > 0
    //     ? Number(formik.values.alarm_timeout?.trim())
    //     : formik.values.alarm_timeout || 0;
    if (formik.values.emailSubscribed) {
      formik.values.snapshotReportCategories = arrayToObject(
        formik.values.snapshotReportCategories
      );
    } else {
      formik.values.snapshotReportCategories = "";
    }

    try {
      if (initialData) {
        await service.updateSites(companyID, [initialData], formik.values);
      } else {
        await service.addSite(companyID, formik.values);
      }
      setIsLoading(false);
      modalRef.current.toggleDialog();
      formik.resetForm();
      initialData = null;
      onClose(true);
    } catch (error) {
      console.error("Error creating Site:", error);
      setIsLoading(false);
      setSnackbarOpen(true);
    }
  }

  const handleCancel = () => {
    modalRef.current.toggleDialog();
    formik.resetForm();
    initialData = null;
    onClose(false);
  };

  const isFormValid = () => {
    return formik.isValid && Object.keys(formik.errors).length === 0;
  };

  const ButtonStyle = {
    backgroundColor: "#0073E6",
    color: "white",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
  };

  const DisabledButtonStyle = {
    ...ButtonStyle,
    backgroundColor: "#E8EBF1",
    color: "#929AA9",
    cursor: "not-allowed",
  };

  const handleCreateOrUpdate = () => {
    formik.setFieldValue("disabled", true);
    formik.handleSubmit();
  };

  // Return null if user doesn't have required permissions
  if ((!canCreateSite && !initialData) || (!canUpdateSite && initialData)) {
    return null;
  }

  return (
    <Dialog
      ref={modalRef}
      width="883px"
      headerStyle={{ display: "none" }}
      bodyStyle={{ padding: "0px", width: "100%" }}
      cardStyle={{ borderRadius: "8px", overflow: "auto" }}
      style={{ cursor: isLoading ? "wait" : "default" }}
      items={[
        <Item
          type="button"
          style={{ ...ButtonStyle, backgroundColor: "#757575" }}
          onClick={handleCancel}
          disabled={isLoading}
        >
          {t("cancel")}
        </Item>,
        <Item
          type="button"
          style={isFormValid() ? ButtonStyle : DisabledButtonStyle}
          onClick={handleCreateOrUpdate}
          disabled={isLoading || !isFormValid()}
        >
          {initialData ? t("update") : t("create")}
        </Item>,
      ]}
    >
      <ModalHeader>
        {!initialData ? t("createSite") : t("editSite")}
      </ModalHeader>
      <ScrollableContent>
        <MainContent>
          <Section>
            <SectionHeader>{t("basicInformation")}</SectionHeader>
            <Row>
              <InputField
                id="siteName"
                name="siteName"
                label={t("siteNameText")}
                fullWidth={true}
                required={true}
                autoComplete="off"
                disabled={!!initialData}
                {...formik.getFieldProps("siteName")}
                onChange={(event) =>
                  formik.setFieldValue("siteName", event.target.value)
                }
                error={formik.touched.siteName && formik.errors.siteName}
                helperText={formik.touched.siteName && formik.errors.siteName}
              />
              <InputField
                id="displaySiteName"
                name="displaySiteName"
                label={t("siteLabel")}
                fullWidth={true}
                required={false}
                autoComplete="off"
                {...formik.getFieldProps("displaySiteName")}
                onChange={(event) =>
                  formik.setFieldValue("displaySiteName", event.target.value)
                }
                error={
                  formik.touched.displaySiteName &&
                  formik.errors.displaySiteName
                }
                helperText={
                  formik.touched.displaySiteName &&
                  formik.errors.displaySiteName
                }
              />
            </Row>
          </Section>
          <Section>
            <SectionHeader>{t("addressText")}</SectionHeader>
            <Row>
              <InputField
                id="address_line"
                name="address_line"
                label={t("address1")}
                fullWidth={true}
                required={true}
                autoComplete="off"
                {...formik.getFieldProps("address_line")}
                onChange={(event) =>
                  formik.setFieldValue("address_line", event.target.value)
                }
                error={
                  formik.touched.address_line && formik.errors.address_line
                }
                helperText={
                  formik.touched.address_line && formik.errors.address_line
                }
              />
              <InputField
                id="address2"
                name="address2"
                label={t("address2")}
                fullWidth={true}
                required={false}
                autoComplete="off"
                {...formik.getFieldProps("address2")}
                onChange={(event) =>
                  formik.setFieldValue("address2", event.target.value)
                }
                error={formik.touched.address2 && formik.errors.address2}
                helperText={formik.touched.address2 && formik.errors.address2}
              />
              <InputField
                id="postal_code"
                name="postal_code"
                label={t("postalCode")}
                fullWidth={true}
                required={false}
                autoComplete="off"
                {...formik.getFieldProps("postal_code")}
                onChange={(event) =>
                  formik.setFieldValue("postal_code", event.target.value)
                }
                error={formik.touched.postal_code && formik.errors.postal_code}
                helperText={
                  formik.touched.postal_code && formik.errors.postal_code
                }
              />
            </Row>
            <Row>
              <InputField
                id="town"
                name="town"
                label={t("town")}
                fullWidth={true}
                required={false}
                autoComplete="off"
                {...formik.getFieldProps("town")}
                onChange={(event) =>
                  formik.setFieldValue("town", event.target.value)
                }
                error={formik.touched.town && formik.errors.town}
                helperText={formik.touched.town && formik.errors.town}
              />
              <InputField
                id="city"
                name="city"
                label={t("city")}
                fullWidth={true}
                required={true}
                autoComplete="off"
                {...formik.getFieldProps("city")}
                onChange={(event) =>
                  formik.setFieldValue("city", event.target.value)
                }
                error={formik.touched.city && formik.errors.city}
                helperText={formik.touched.city && formik.errors.city}
              />
              <InputField
                id="country"
                name="country"
                label={t("country")}
                fullWidth={true}
                required={true}
                autoComplete="off"
                {...formik.getFieldProps("country")}
                onChange={(event) =>
                  formik.setFieldValue("country", event.target.value)
                }
                error={formik.touched.country && formik.errors.country}
                helperText={formik.touched.country && formik.errors.country}
              />
            </Row>
          </Section>
          <Section>
            <SectionHeader>{t("contactInformation")}</SectionHeader>
            <Row>
              <InputField
                id="contact_first_name"
                name="contact_first_name"
                label={t("firstName")}
                fullWidth={true}
                required={false}
                autoComplete="off"
                {...formik.getFieldProps("contact_first_name")}
                onChange={(event) =>
                  formik.setFieldValue("contact_first_name", event.target.value)
                }
                error={
                  formik.touched.contact_first_name &&
                  formik.errors.contact_first_name
                }
                helperText={
                  formik.touched.contact_first_name &&
                  formik.errors.contact_first_name
                }
              />
              <InputField
                id="contact_last_name"
                name="contact_last_name"
                label={t("lastName")}
                fullWidth={true}
                required={false}
                autoComplete="off"
                {...formik.getFieldProps("contact_last_name")}
                onChange={(event) =>
                  formik.setFieldValue("contact_last_name", event.target.value)
                }
                error={
                  formik.touched.contact_last_name &&
                  formik.errors.contact_last_name
                }
                helperText={
                  formik.touched.contact_last_name &&
                  formik.errors.contact_last_name
                }
              />
              <InputField
                id="contact_mobile_number"
                name="contact_mobile_number"
                label={t("phoneNo")}
                fullWidth={true}
                required={false}
                autoComplete="off"
                {...formik.getFieldProps("contact_mobile_number")}
                onChange={(event) =>
                  formik.setFieldValue(
                    "contact_mobile_number",
                    event.target.value
                  )
                }
                error={
                  formik.touched.contact_mobile_number &&
                  formik.errors.contact_mobile_number
                }
                helperText={
                  formik.touched.contact_mobile_number &&
                  formik.errors.contact_mobile_number
                }
              />
            </Row>
            <Row>
              <InputField
                id="contact_phone_number"
                name="contact_phone_number"
                label={t("mobileNo")}
                fullWidth={true}
                required={false}
                autoComplete="off"
                {...formik.getFieldProps("contact_phone_number")}
                onChange={(event) =>
                  formik.setFieldValue(
                    "contact_phone_number",
                    event.target.value
                  )
                }
                error={
                  formik.touched.contact_phone_number &&
                  formik.errors.contact_phone_number
                }
                helperText={
                  formik.touched.contact_phone_number &&
                  formik.errors.contact_phone_number
                }
              />
              <InputField
                id="contact_extension"
                name="contact_extension"
                label={t("extension")}
                fullWidth={true}
                required={false}
                autoComplete="off"
                {...formik.getFieldProps("contact_extension")}
                onChange={(event) =>
                  formik.setFieldValue("contact_extension", event.target.value)
                }
                error={
                  formik.touched.contact_extension &&
                  formik.errors.contact_extension
                }
                helperText={
                  formik.touched.contact_extension &&
                  formik.errors.contact_extension
                }
              />
              <InputField
                id="contact_country"
                name="contact_country"
                label={t("contactCountry")}
                fullWidth={true}
                required={false}
                autoComplete="off"
                {...formik.getFieldProps("contact_country")}
                onChange={(event) =>
                  formik.setFieldValue("contact_country", event.target.value)
                }
                error={
                  formik.touched.contact_country &&
                  formik.errors.contact_country
                }
                helperText={
                  formik.touched.contact_country &&
                  formik.errors.contact_country
                }
              />
            </Row>
          </Section>
          <Section>
            <SectionHeader>{t("others")}</SectionHeader>
            {deviceRegistrationType == 2 && (
              <Row>
                <InputField
                  id="startIPAddress"
                  name="startIPAddress"
                  label={t("site.startIPAddress")}
                  fullWidth={true}
                  required={true}
                  autoComplete="off"
                  {...formik.getFieldProps("startIPAddress")}
                  onChange={(event) =>
                    formik.setFieldValue("startIPAddress", event.target.value)
                  }
                  error={
                    formik.touched.startIPAddress &&
                    formik.errors.startIPAddress
                  }
                  helperText={
                    formik.touched.startIPAddress &&
                    formik.errors.startIPAddress
                  }
                />
                <InputField
                  id="endIPAddress"
                  name="endIPAddress"
                  label={t("site.endIPAddress")}
                  fullWidth={true}
                  required={true}
                  autoComplete="off"
                  {...formik.getFieldProps("endIPAddress")}
                  onChange={(event) =>
                    formik.setFieldValue("endIPAddress", event.target.value)
                  }
                  error={
                    formik.touched.endIPAddress && formik.errors.endIPAddress
                  }
                  helperText={
                    formik.touched.endIPAddress && formik.errors.endIPAddress
                  }
                />
              </Row>
            )}
            <Row container spacing={2}>
              {/* <Grid item xs={6}>
                <InputField
                  id="alarm_timeout"
                  name="alarm_timeout"
                  label={t("alarmTimeout")}
                  type="number"
                  fullWidth={true}
                  required={true}
                  autoComplete="off"
                  min="0"
                  step={1}
                  {...formik.getFieldProps("alarm_timeout")}
                  onChange={(event) => {
                    const value = event.target.value;
                    // DGN-887 : Allow only positive numbers and empty input
                    if (value === '' || parseInt(value, 10) >= 0) {
                      formik.setFieldValue("alarm_timeout", value);
                    }
                  }}
                  onKeyDown={(event) => {
                    // DGN-887 : Prevent negative sign input and other unwanted keys
                    if (event.key === '-' || event.key === 'e') {
                      event.preventDefault();
                    }
                  }}
                  error={
                    formik.touched.alarm_timeout && formik.errors.alarm_timeout
                  }
                  helperText={
                    formik.touched.alarm_timeout && formik.errors.alarm_timeout
                  }
                />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginTop: "8px",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.alarm_timeout_inherited}
                        name="alarm_timeout_inherited"
                        id="alarm_timeout_inherited"
                        onChange={(event) =>
                          formik.setFieldValue(
                            "alarm_timeout_inherited",
                            event.target.checked
                          )
                        }
                      />
                    }
                    label={t("inherited")}
                  />
                </div>
              </Grid> */}
              <Grid item xs={6}>
                <DropdownField
                  id="timeZone"
                  name="timeZone"
                  label={t("timeZone")}
                  fullWidth={true}
                  required={true}
                  options={timeZoneOptions}
                  value={formik.values.timeZone}
                  onChange={(event) =>
                    formik.setFieldValue("timeZone", event.target.value)
                  }
                  error={formik.touched.timeZone && formik.errors.timeZone}
                  helperText={formik.touched.timeZone && formik.errors.timeZone}
                />
              </Grid>
            </Row>
          </Section>
          <Section>
            <SectionHeader>{t("emailNotification")}</SectionHeader>
            <Row>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <label>{t("subscribeReportsViaEmail")}</label>
                <FormControlLabel
                  control={
                    <Switch
                      disabled={
                        !canCreateEmailConfiguration &&
                        !canUpdateEmailConfiguration &&
                        !canDeleteEmailConfiguration
                      }
                      checked={formik.values.emailSubscribed}
                      onChange={(val) => {
                        formik.setFieldValue("emailSubscribed", val);
                      }}
                      name="emailSubscribed"
                    />
                  }
                  label={
                    <span style={{ display: "none" }}>
                      {t("subscribeReportsViaEmail")}
                    </span>
                  }
                  labelPlacement="start"
                  style={{ marginLeft: "0" }}
                />
              </div>
            </Row>
            {formik.values.emailSubscribed && (
              <>
                <Row>
                  <Grid item xs={6}>
                    <SiteReportedTime
                      disabled={
                        !canCreateEmailConfiguration &&
                        !canUpdateEmailConfiguration &&
                        !canDeleteEmailConfiguration
                      }
                      label={t("generateEODReportsAt")}
                      fullWidth={true}
                      required={true}
                      reportTime={formik.values.scheduledReportTime}
                      onReportingTimeChanged={(reportTime) =>
                        formik.setFieldValue("scheduledReportTime", reportTime)
                      }
                      error={
                        formik.touched.scheduledReportTime &&
                        formik.errors.scheduledReportTime
                      }
                      helperText={
                        formik.touched.scheduledReportTime &&
                        formik.errors.scheduledReportTime
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <DropdownField
                      disabled={
                        !canCreateEmailConfiguration &&
                        !canUpdateEmailConfiguration &&
                        !canDeleteEmailConfiguration
                      }
                      id="selectReports"
                      name="selectReports"
                      label={t("selectReports")}
                      fullWidth={true}
                      required={true}
                      options={categoriesData}
                      value={formik.values.snapshotReportCategories}
                      multiSelect={true}
                      onChange={(event) =>
                        formik.setFieldValue("snapshotReportCategories", event)
                      }
                      error={
                        //formik.touched.snapshotReportCategories &&
                        formik.errors.snapshotReportCategories
                      }
                      helperText={
                        //formik.touched.snapshotReportCategories &&
                        formik.errors.snapshotReportCategories
                      }
                      showDLR={showDLR}
                    />
                  </Grid>
                </Row>
                <Row>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FieldWrapper
                        label={t("site.emailList")}
                        error={
                          formik.touched.emailList && formik.errors.emailList
                        }
                        helperText={
                          formik.touched.emailList && formik.errors.emailList
                        }
                        fullWidth={true}
                        required={true}
                      >
                        <StyledTextField
                          disabled={
                            !canCreateEmailConfiguration &&
                            !canUpdateEmailConfiguration &&
                            !canDeleteEmailConfiguration
                          }
                          id="emailList"
                          name="emailList"
                          rows={2}
                          maxRows={2}
                          multiline
                          variant="outlined"
                          fullWidth={true}
                          required={true}
                          error={
                            formik.touched.emailList && formik.errors.emailList
                          }
                          onChange={(event) =>
                            formik.setFieldValue(
                              "emailList",
                              event.target.value
                            )
                          }
                          {...formik.getFieldProps("emailList")}
                        />
                      </FieldWrapper>
                    </Grid>
                  </Grid>
                </Row>
                <div>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.attachCSV}
                        name="attachCSV"
                        id="attachCSV"
                        onChange={(event) =>
                          formik.setFieldValue(
                            "attachCSV",
                            event.target.checked
                          )
                        }
                      />
                    }
                    label={t("site.attachCSV")}
                  />
                </div>
              </>
            )}
          </Section>
        </MainContent>
      </ScrollableContent>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        onClose={() => setSnackbarOpen(false)}
      >
        <SnackbarContent error={true}>
          {initialData ? (
            <>{t("Error updating site")}</>
          ) : (
            <>{t("Error creating site")}</>
          )}
        </SnackbarContent>
      </Snackbar>
    </Dialog>
  );
}
